import { useMemo } from 'react';
import { ElectionDesignation } from '@signalvote/shared';
import type { ComplianceProfileDto, FinanceSettingsDto } from '@signalvote/shared';

/**
 * #144 — the ONE shared election-designation options derivation, consumed by BOTH the
 * Add-Contribution picker and the bank-transaction classify picker, so the two dropdowns can
 * never drift (this folds in the #143 shared-hook extraction). It returns a GENERAL list of
 * options — "own cycle + next cycle" is just the current population of that list. Adding a
 * further cycle, a runoff, or a special election later is an additive entry here, with no picker
 * restructure.
 *
 * On a FEDERAL campaign the list is multi-cycle (the campaign's own cycle PLUS the next cycle,
 * every option carrying its year); on Alabama / Virginia it is exactly today's single-cycle list
 * (plain labels), so the #143 extraction is a no-behavior-change refactor there. The federal
 * gate is the jurisdiction's own declared capability, a sibling of the one the picker uses for
 * the Federal-Transfer contribution type
 * (the ruleset's declared `multiCycleElectionDesignations`), read ONCE here — no per-render
 * jurisdiction branch scatters through the UI.
 */
export interface ElectionOption {
  /** The designation stored on the contribution (`Primary` / `General` / `Undesignated`). */
  designation: ElectionDesignation;
  /**
   * The year STORED on the contribution: `null` for the campaign's own current cycle (the filing
   * falls back to the finance-settings date, keeping single-cycle filings byte-identical) and the
   * explicit year for an off-cycle (next-cycle) designation.
   */
  electionYear: number | null;
  /** The display label. Federal: year-first ("2024 General"). AL/VA: plain ("General"). */
  label: string;
  /**
   * Composite stable identity for the `<select>` value. The own-cycle option (stores a null year
   * but displays the settings year) and the next-cycle option (stores the explicit year) share a
   * designation, so a bare designation could not tell them apart — the key encodes both.
   */
  key: string;
}

/** "Next cycle" = the current cycle + 2 years — a House general is always an even year, so this is
 *  exact for a House seat. Senate/President (6/4 years out) are a documented limitation: the picker
 *  offers one extra option they wouldn't select, and the FEC election code is a soft field. */
const NEXT_CYCLE_OFFSET_YEARS = 2;

/**
 * The composite identity for an option / a selected `{designation, electionYear}` pair. Own-cycle
 * (null year) keys as `<designation>:own`; an off-cycle option keys as `<designation>:<year>`.
 */
export function electionOptionKey(
  designation: ElectionDesignation,
  electionYear: number | null,
): string {
  return `${designation}:${electionYear ?? 'own'}`;
}

/** Parse the four-digit year out of a date-only ISO string ("YYYY-MM-DD"); null when absent /
 *  malformed. Slicing the leading four chars is timezone-safe (no `new Date()` round-trip). */
function yearOf(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const year = Number.parseInt(dateStr.slice(0, 4), 10);
  return Number.isFinite(year) ? year : null;
}

function offersMultipleCycles(profile: ComplianceProfileDto | null | undefined): boolean {
  // #711 — the jurisdiction's DECLARED capability. This was previously `federalTransferLimit !=
  // null`, described as "the canonical is-this-federal signal" — but the serve path never assigned
  // that field, so it was null on every response and federal campaigns silently got the
  // single-cycle list. A missing profile resolves to the single-cycle list rather than showing
  // next-cycle options that may not apply.
  return profile?.multiCycleElectionDesignations === true;
}

/**
 * Pure derivation behind {@link useElectionDesignationOptions} — exported so it can be unit-tested
 * without React. See the interface docs above for the federal vs AL/VA shapes.
 */
export function deriveElectionOptions(
  settings: FinanceSettingsDto | null | undefined,
  profile: ComplianceProfileDto | null | undefined,
): ElectionOption[] {
  // Where the receipt date decides the election there is nothing to choose, so the pickers render
  // no field. A profile still loading keeps the list.
  if (profile?.offersElectionDesignation === false) return [];

  const ownOption = (designation: ElectionDesignation, ownYear: number | null): ElectionOption => ({
    designation,
    electionYear: null,
    // Federal always shows the year (year-first) to disambiguate against the next cycle; AL/VA
    // (or an unparseable date) render the plain designation — byte-identical to today's list.
    label: offersMultipleCycles(profile) && ownYear != null ? `${ownYear} ${designation}` : designation,
    key: electionOptionKey(designation, null),
  });

  const nextOption = (designation: ElectionDesignation, nextYear: number): ElectionOption => ({
    designation,
    electionYear: nextYear,
    label: `${nextYear} ${designation}`,
    key: electionOptionKey(designation, nextYear),
  });

  const primaryYear = yearOf(settings?.primaryDate);
  const generalYear = yearOf(settings?.generalDate);
  // ADR-076 — a primary runoff and a general runoff share the one Runoff option; the server names
  // the runoff a gift is for by its date. The option's year is the earlier runoff's, the cycle the
  // runoffs belong to (a Georgia general runoff can fall in the following January).
  const runoffYears = [settings?.primaryRunoffDate, settings?.generalRunoffDate]
    .map(yearOf)
    .filter((year): year is number => year != null);
  const runoffYear = runoffYears.length > 0 ? Math.min(...runoffYears) : null;
  const hasPrimary = settings?.primaryDate != null;
  const hasGeneral = settings?.generalDate != null;
  // A runoff is only offered when the campaign has actually set a runoff date
  // (AL / GA / Federal can). It mirrors Primary/General uniformly — own cycle here,
  // next cycle in the federal block below — so no picker special-cases it.
  const hasRunoff = settings?.primaryRunoffDate != null || settings?.generalRunoffDate != null;

  const options: ElectionOption[] = [];

  // Own cycle first (Primary, General, then Runoff), matching today's ordering for AL/VA.
  if (hasPrimary) options.push(ownOption(ElectionDesignation.Primary, primaryYear));
  if (hasGeneral) options.push(ownOption(ElectionDesignation.General, generalYear));
  if (hasRunoff) options.push(ownOption(ElectionDesignation.Runoff, runoffYear));

  // Then the next cycle — federal only, and only when we could derive the own year to offset from.
  if (offersMultipleCycles(profile)) {
    if (hasPrimary && primaryYear != null) {
      options.push(nextOption(ElectionDesignation.Primary, primaryYear + NEXT_CYCLE_OFFSET_YEARS));
    }
    if (hasGeneral && generalYear != null) {
      options.push(nextOption(ElectionDesignation.General, generalYear + NEXT_CYCLE_OFFSET_YEARS));
    }
    if (hasRunoff && runoffYear != null) {
      options.push(nextOption(ElectionDesignation.Runoff, runoffYear + NEXT_CYCLE_OFFSET_YEARS));
    }
  }

  // Undesignated is always last and always plain (no year — it emits no election code).
  options.push(ownOption(ElectionDesignation.Undesignated, null));

  return options;
}

/**
 * React hook wrapper around {@link deriveElectionOptions} — memoized on the inputs that change the
 * list. Consumed by both contribution pickers.
 */
export function useElectionDesignationOptions(
  settings: FinanceSettingsDto | null | undefined,
  profile: ComplianceProfileDto | null | undefined,
): ElectionOption[] {
  return useMemo(
    () => deriveElectionOptions(settings, profile),
    // Depend on the exact primitive fields the derivation reads, not the whole `settings`/`profile`
    // objects (whose reference changes each render) — this is precise and avoids needless recomputes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings?.primaryDate, settings?.generalDate, settings?.primaryRunoffDate, settings?.generalRunoffDate, profile?.multiCycleElectionDesignations, profile?.offersElectionDesignation],
  );
}
